import { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Close } from '@mui/icons-material'
import { Card, CardContent, Chip, IconButton, Skeleton, Stack, Typography } from '@mui/material'

import { GameIcon } from '../../../../../features/game'
import { InternalGameDocument } from '../../../games/types/InternalGame'

type AnalysisGameBlockProps = {
  title: string
  game?: InternalGameDocument
  marketIso: string
  isLoading?: boolean
  error?: ReactNode
  removeLabel?: string
  onRemove?: () => void
}

export const AnalysisGameBlock: FC<AnalysisGameBlockProps> = ({ title, game, marketIso, isLoading, error, removeLabel, onRemove }) => {
  const { t } = useTranslation()

  return (
    <Card variant="outlined" sx={{ minWidth: 240, flex: '1 1 240px' }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="overline">{title}</Typography>
          {onRemove && (
            <IconButton size="small" aria-label={removeLabel} onClick={onRemove}>
              <Close fontSize="small" />
            </IconButton>
          )}
        </Stack>
        {isLoading ? (
          <Skeleton variant="rectangular" height={64} />
        ) : (
          <Stack direction="row" spacing={2} alignItems="flex-start">
            {game && <GameIcon src={game.icons?.length ? game.icons : game.icon ?? undefined} gameName={game.name ?? ''} size="small" />}
            <Stack spacing={0.5} minWidth={0}>
              {game && (
                <>
                  <Typography fontWeight="bold">
                    {game.name}{' '}
                    <Typography component="span" color="text.secondary" textTransform="uppercase">
                      ({marketIso})
                    </Typography>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {game.artist || '---'}
                  </Typography>
                  <Typography variant="body2">
                    {t('internal-analysts:analysis_overview_genre')}: {game.genre || '-'}
                  </Typography>
                  <Typography variant="body2">
                    {t('internal-analysts:analysis_overview_subgenre_meta')}: {game.subGenre || '-'}
                  </Typography>
                  {game.analysisOutdated && (
                    <Chip size="small" color="warning" label={t('internal-analysts:analysis_overview_outdated')} sx={{ alignSelf: 'flex-start' }} />
                  )}
                </>
              )}
              {error}
            </Stack>
          </Stack>
        )}
      </CardContent>
    </Card>
  )
}
